import type { Email } from "../../services/graphMailService";

/**
 * J1 — In-Fenster-Navigation der geöffneten Mail (rein und testbar).
 *
 * Aus der rechten Leiste heraus entsteht KEIN neues Fenster: der Fensterinhalt
 * wechselt auf die angeklickte Mail und ein Zurück-Pfeil führt zurück. Das ist
 * die strukturelle Bremse gegen eine Fenster-Lawine; die Regeln von `navPush`
 * sind die Bremse gegen Ping-Pong und Endlostiefe.
 */

export type MailNavEntry = { mailbox: string; email: Email };

/** Tiefer als acht Ebenen geht es nicht — der älteste Eintrag fällt heraus. */
export const MAX_NAV_TIEFE = 8;

/** Stabiler Schlüssel einer Mail: Postfach (normalisiert) + Graph-Message-Id. */
export function navKey(e: { mailbox: string; email: { id: string } }): string {
    return `${String(e.mailbox ?? "").trim().toLowerCase()}:${String(e.email?.id ?? "")}`;
}

/** Oberster (= angezeigter) Eintrag des Stapels. */
export function navTop(stack: MailNavEntry[]): MailNavEntry {
    return stack[stack.length - 1];
}

/**
 * Eine Mail öffnen:
 * - dieselbe Mail wie oben ⇒ Stapel unverändert (Selbstklick tut nichts),
 * - Mail liegt schon weiter unten ⇒ Rückschnitt auf diese Ebene (A→B→A ergibt [A]),
 * - sonst anhängen; bei `MAX_NAV_TIEFE` fällt der älteste Eintrag heraus.
 */
export function navPush(stack: MailNavEntry[], e: MailNavEntry): MailNavEntry[] {
    const key = navKey(e);
    if (stack.length > 0 && navKey(navTop(stack)) === key) return stack;
    const idx = stack.findIndex((s) => navKey(s) === key);
    if (idx >= 0) return stack.slice(0, idx + 1);
    const next = [...stack, e];
    return next.length > MAX_NAV_TIEFE ? next.slice(next.length - MAX_NAV_TIEFE) : next;
}

/** Eine Ebene zurück. Auf einem einelementigen Stapel ein No-op. */
export function navPop(stack: MailNavEntry[]): MailNavEntry[] {
    return stack.length <= 1 ? stack : stack.slice(0, stack.length - 1);
}

/* ── Fenster-Registrierung ──────────────────────────────────────────────
   Welche Mail zeigt welches Fenster gerade? Modul-lokal (kein React-State),
   damit `openMailDetailWindow` vor dem Öffnen fragen kann: existiert schon ein
   Fenster mit genau diesem Inhalt? Nötig, weil die Fenster-Id aus der ZUERST
   geöffneten Mail entsteht — nach einer In-Fenster-Navigation zeigt ein Fenster
   also eine andere Mail, als seine Id vermuten lässt. */

const fensterNachKey = new Map<string, string>();

/** Registriert, welche Mail `windowId` gerade zeigt. Gibt die Abmeldung zurück. */
export function registriereMailFenster(windowId: string, key: string): () => void {
    fensterNachKey.set(windowId, key);
    return () => {
        if (fensterNachKey.get(windowId) === key) fensterNachKey.delete(windowId);
    };
}

/** Fenster, das diese Mail zeigt — bei mehreren das zuletzt registrierte. */
export function findeMailFenster(key: string): string | null {
    let treffer: string | null = null;
    for (const [windowId, k] of fensterNachKey) {
        if (k === key) treffer = windowId;
    }
    return treffer;
}

/** Nur für Tests: Registrierung leeren. */
export function _leereMailFensterRegistrierung(): void {
    fensterNachKey.clear();
}
